import { Navigate } from 'react-router-dom'
import { useIsAuthenticated } from '@azure/msal-react'
import { useAuth } from '@/providers/AuthProvider'
import { hasRole, hasAnyPermission } from '@/auth/permissions'
import { WORKFORCE_ROLES, WORKFORCE_PERMISSION_KEYS } from '@/lib/workforcePermissions'
import { canSeeTalentDashboard } from '@/lib/talentDashboardPermissions'
import { Loader2 } from 'lucide-react'
import { ReactNode } from 'react'

interface WorkforceRouteProps {
  children: ReactNode
}

/**
 * Route guard for the Workforce Explorer.
 *
 * A NARROWED AdminRoute: same loading discipline, different decision. AdminRoute
 * admits anyone holding ANY grant (hasAnyGrant), which is right for the admin
 * shell but wrong here — and reusing it would work in reverse too, letting a
 * workforce-only grantee into /admin, /organization, /role-master and /history
 * for a page of 403 toasts. This guard admits exactly the console roles and the
 * two workforce keys; everyone else goes to /employee-journey.
 *
 * The server is still the control: every request this page makes is authorised
 * again in identity-service. This only decides what to render.
 */
export function WorkforceRoute({ children }: WorkforceRouteProps) {
  const { user, arePermissionsLoading } = useAuth()
  const isAuthenticated = useIsAuthenticated()

  // MANDATORY wait. On hard refresh MSAL rehydrates the account synchronously,
  // but the /auth/me fetch that carries roles AND grants is async. A user whose
  // only access is a workforce:explorer grant looks unauthorised until those
  // grants land — deciding now would bounce them off the very page the grant
  // unlocks, and the redirect would stick for the whole session.
  //
  // Deliberately NOT gated on `isAuthenticated`: grants can be in flight for a
  // user MSAL does not report as signed in (dev personas, and any path where the
  // account rehydrates after the profile does). AdminRoute couples this wait to
  // isAuthenticated and therefore decides early in exactly those cases — the bug
  // is masked there because it admits ANY grant-holder. Here the wait is the only
  // thing standing between a legitimate grantee and a sticky redirect, so it must
  // hold whenever permissions are still loading for a known user.
  if (user !== null && arePermissionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        <span className="text-sm">Loading…</span>
      </div>
    )
  }

  if (isAuthenticated && user === null) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] text-gray-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        <span className="text-sm">Loading…</span>
      </div>
    )
  }

  // Dashboards: the Workforce Explorer's roles and keys, or the talent dashboard's
  // (Victor, 9 Oct 2026). DashboardsPage then shows each person only the sections
  // they may see, so a talent-only grantee never mounts the Workforce Explorer.
  if (!hasRole(user, WORKFORCE_ROLES) && !hasAnyPermission(user, WORKFORCE_PERMISSION_KEYS) && !canSeeTalentDashboard(user)) {
    return <Navigate to="/employee-journey" replace />
  }

  return <>{children}</>
}
